import { useEffect, useState } from "react";
import { Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, applyTheme } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { Field, PageTitle, AdminButton, useList } from "@/components/admin/kit";
import { useAuth } from "@/lib/auth";
import { BookingHours } from "@/components/admin/BookingHours";
import { useUnsaved, UnsavedBadge, same } from "@/lib/unsaved";
import { useTerms } from "@/lib/industry";
import { TempPassword } from "@/components/admin/TempPassword";
import { CtaLinksEditor } from "@/components/admin/CtaLinksEditor";
import { VisitInfoEditor } from "@/components/admin/VisitInfoEditor";

function Branding() {
  const [b, setB] = useState(null);
  const [orig, setOrig] = useState(null);
  const { sells, ind } = useTerms();
  useEffect(() => { api.get("/admin/builder").then((r) => { setB(r.data); setOrig(r.data); }); }, []);
  const dirty = !!b && !same(b, orig);
  useUnsaved(dirty);
  if (!b) return null;
  const set = (k) => (e) => setB({ ...b, [k]: e.target.value });
  const save = async () => {
    try { const { data } = await api.put("/admin/builder", b); setB(data); setOrig(data); applyTheme(data); toast.success("Branding saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-3xl bg-white border p-6 grid sm:grid-cols-2 gap-4" data-testid="branding-form">
      <Field label={sells ? "Builder name" : "Business name"}><Input value={b.name} onChange={set("name")} data-testid="branding-name" /></Field>
      <Field label="URL slug"><Input value={b.slug} onChange={set("slug")} data-testid="branding-slug" /></Field>
      <Field label="Tagline" className="sm:col-span-2"><Input value={b.tagline} onChange={set("tagline")} data-testid="branding-tagline" /></Field>
      <Field label={sells ? "Logo (shown on your pages, tour header, PDFs and emails)" : "Logo (shown on your pages, tour header and emails)"}><ImageUpload value={b.logo_url} onChange={(v) => setB({ ...b, logo_url: v })} testid="branding-logo" /></Field>
      <Field label={`Icon (small square mark used inside hotspot and ${sells ? "floor plan" : "plan"} pins)`}><ImageUpload value={b.icon_url || ""} onChange={(v) => setB({ ...b, icon_url: v })} testid="branding-icon" /><p className="text-xs text-slate-500 mt-1">Square image, e.g. 256×256 PNG. Turn it on under Tour skin → "My icon inside hotspot and {sells ? "floor plan" : "plan"} pins".</p></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Primary colour"><input type="color" value={b.primary_color} onChange={set("primary_color")} className="w-full h-12 rounded-lg border" data-testid="branding-primary" /></Field>
        <Field label="Accent colour"><input type="color" value={b.accent_color} onChange={set("accent_color")} className="w-full h-12 rounded-lg border" data-testid="branding-accent" /></Field>
      </div>
      <Field label="Sales team email (new-lead notifications)"><Input type="email" value={b.notify_email} onChange={set("notify_email")} data-testid="branding-notify-email" /></Field>
      <Field label="Sales phone"><Input value={b.phone} onChange={set("phone")} data-testid="branding-phone" /></Field>
      {sells && <><div className="sm:col-span-2 rounded-2xl bg-slate-50 border p-4 space-y-3" data-testid="design-studio-settings">
        <label className="flex items-start gap-3 text-sm">
          <Switch checked={!!b.design_studio_mode} onCheckedChange={(v) => setB({ ...b, design_studio_mode: v })} data-testid="branding-design-studio-toggle" />
          <span><strong>Design studio mode</strong><br /><span className="text-slate-500">Hide the buyer finish picker and show a design studio appointment button instead. Your design team can still manage finishes and hotspots.</span></span>
        </label>
        {b.design_studio_mode && <Field label="Button / form title"><Input value={b.design_studio_label || ""} onChange={set("design_studio_label")} data-testid="branding-design-studio-label" /></Field>}
      </div>
      <div className="sm:col-span-2 rounded-2xl bg-slate-50 border p-4 grid sm:grid-cols-3 gap-3" data-testid="tax-settings">
        <Field label="Tax name"><Input value={b.tax_label ?? "GST"} onChange={set("tax_label")} data-testid="branding-tax-label" /></Field>
        <Field label="Tax rate (%)"><Input type="number" step="0.1" value={b.tax_rate ?? 5} onChange={(e) => setB({ ...b, tax_rate: +e.target.value })} data-testid="branding-tax-rate" /></Field>
        <label className="flex items-center gap-3 text-sm pt-5"><Switch checked={!!b.prices_include_tax} onCheckedChange={(v) => setB({ ...b, prices_include_tax: v })} data-testid="branding-tax-included" />Prices already include tax</label>
        <Field label="Rebate note on quotes (leave blank to hide)" className="sm:col-span-3"><Input value={b.rebate_note ?? "You may be eligible for a GST/HST new housing rebate. Ask our sales team for details."} onChange={set("rebate_note")} data-testid="branding-rebate-note" /></Field>
      </div></>}
      <VisitInfoEditor b={b} setB={setB} />
      <div className="sm:col-span-2 rounded-2xl bg-slate-50 border p-4 space-y-2" data-testid="cta-settings">
        <p className="text-sm font-semibold">Booking buttons in your tours</p>
        <CtaLinksEditor value={b.cta_links} onChange={(v) => setB({ ...b, cta_links: v })} hint={`The first button is the main call to action everywhere (top bar, every ${sells ? "room" : "suite"}, the assistant, your public page). It opens your own booking page in a new tab, and each click is logged under Enquiries. Got more than one location? Add the same button once per location (same text, its own link, and the location name) and guests will pick their location before booking. Leave empty to use the built-in booking form.`} />
        {ind === "fitness" && <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t mt-3">
          <label className="text-sm flex items-center gap-2">Show "Booked X times this week" on a suite from
            <Input type="number" min={0} max={100} value={b.booked_badge_min ?? 2} onChange={(e) => setB({ ...b, booked_badge_min: Math.max(0, Math.min(100, parseInt(e.target.value || "0", 10))) })} className="w-20 h-9" data-testid="booked-badge-min" />
            clicks <span className="text-xs text-slate-500">(0 = off)</span></label>
          <label className="text-sm flex items-center gap-2"><Switch checked={b.weekly_booking_email !== false} onCheckedChange={(v) => setB({ ...b, weekly_booking_email: v })} data-testid="weekly-booking-email" />Email admins a Monday summary of last week's booking clicks</label>
        </div>}
      </div>
      <BookingHours value={b.booking} onChange={(v) => setB({ ...b, booking: v })} />
      <div className="sm:col-span-2 flex justify-end items-center gap-3"><UnsavedBadge show={dirty} /><AdminButton onClick={save} data-testid="branding-save">Save branding</AdminButton></div>
    </div>
  );
}

const ROLES = [["admin", "Admin — everything"], ["content", "Content — homes, lots, hotspots, finishes"], ["design", "Design — finishes & hotspots"], ["sales", "Sales — leads & analytics"]];
const ROLES_OTHER = [["admin", "Admin — everything"], ["content", "Content — tours, sites & hotspots"], ["design", "Design — hotspots"], ["sales", "Sales — enquiries & analytics"]];
const RoleSelect = ({ value, onChange, testid }) => {
  const { sells } = useTerms();
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-md border px-2 text-sm bg-white w-full" data-testid={testid}>
      {(sells ? ROLES : ROLES_OTHER).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
  );
};

function Team() {
  const { user } = useAuth();
  const [users, reload] = useList("/admin/users");
  const [f, setF] = useState({ name: "", email: "", password: "", role: "sales" });
  const setRole = async (u, role) => {
    try { await api.put(`/admin/users/${u.id}`, { role }); toast.success("Role updated"); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/users", f); toast.success("Account created"); setF({ name: "", email: "", password: "", role: "sales" }); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (u) => { if (window.confirm(`Remove ${u.email}?`)) { await api.delete(`/admin/users/${u.id}`); reload(); } };
  return (
    <div className="rounded-3xl bg-white border p-6" data-testid="team-section">
      <div className="space-y-2 mb-5">
        {(users || []).map((u) => (
          <div key={u.id} className="flex flex-wrap items-center justify-between border-b py-2 text-sm" data-testid={`team-user-${u.id}`}>
            <span className="flex-1"><strong>{u.name || u.email}</strong> <span className="text-slate-500">{u.email}</span>{u.support && <span className="ml-2 text-[10px] uppercase tracking-widest bg-sky-100 text-sky-800 rounded-full px-2 py-0.5" title="Added by Showhome Interactive so we can help you. Remove it any time." data-testid={`team-user-support-${u.id}`}>Support</span>}</span>
            <span className="w-64 mx-2">{u.id !== user.id ? <RoleSelect value={u.tenant_role || "admin"} onChange={(r) => setRole(u, r)} testid={`team-user-role-${u.id}`} /> : u.tenant_role}</span>
            {u.id !== user.id && <TempPassword user={u} url={`/admin/users/${u.id}/password`} testid={`team-user-password-${u.id}`} />}
            {u.id !== user.id && <button onClick={() => del(u)} className="p-2 text-slate-400 hover:text-rose-600" data-testid={`team-user-delete-${u.id}`}><Trash2 className="w-4 h-4" /></button>}
          </div>
        ))}
        {users?.length === 0 && <p className="text-sm text-slate-400">No team accounts yet.</p>}
      </div>
      <form onSubmit={add} className="grid sm:grid-cols-5 gap-3 items-end">
        <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="team-new-name" /></Field>
        <Field label="Email"><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="team-new-email" /></Field>
        <Field label="Temporary password (8+)"><Input required minLength={8} type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="team-new-password" /></Field>
        <Field label="Role"><RoleSelect value={f.role} onChange={(r) => setF({ ...f, role: r })} testid="team-new-role" /></Field>
        <AdminButton type="submit" data-testid="team-new-submit"><UserPlus className="w-4 h-4" />Create account</AdminButton>
      </form>
    </div>
  );
}

function NewBuilder() {
  const [f, setF] = useState({ name: "", slug: "" });
  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/builders", { ...f, notify_email: "" });
      localStorage.setItem("ih_builder", data.id);
      toast.success("Builder created");
      window.location.reload();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={add} className="rounded-3xl bg-white border p-6 grid sm:grid-cols-3 gap-3 items-end" data-testid="new-builder-form">
      <Field label="Builder name"><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-builder-name" /></Field>
      <Field label="URL slug"><Input required value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} data-testid="new-builder-slug" /></Field>
      <AdminButton type="submit" data-testid="new-builder-submit">Create builder</AdminButton>
    </form>
  );
}

function Enquiries() {
  const [list] = useList("/admin/enquiries");
  return (
    <div className="rounded-3xl bg-white border p-6 space-y-3" data-testid="enquiries-list">
      {(list || []).map((e) => (
        <div key={e.id} className="border-b pb-3 text-sm" data-testid={`enquiry-${e.id}`}>
          <p><strong>{e.name}</strong> {e.company && `· ${e.company}`} <span className="text-slate-400">{e.created_at?.slice(0, 10)}</span></p>
          <p className="text-slate-500">{e.email}{e.phone ? ` · ${e.phone}` : ""}</p>
          {e.message && <p className="mt-1">{e.message}</p>}
        </div>
      ))}
      {list?.length === 0 && <p className="text-sm text-slate-400">No demo requests yet.</p>}
    </div>
  );
}

export default function AdminSettings() {
  const { user } = useAuth();
  return (
    <div className="space-y-10">
      <div><PageTitle help="settings" title="Branding" sub="Your logo and colours theme the public site, 3D viewer and PDF quotes" /><Branding /></div>
      <div><PageTitle title="Team" sub="Accounts can only be created here — there is no public sign-up. Each role only sees its own sections." /><Team /></div>
      {user?.platform_role === "superadmin" && <p className="text-sm text-slate-500">Builders and demo requests are managed under <strong>Platform — all builders</strong>.</p>}
    </div>
  );
}
